import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Box, Card, TextField, Button, Typography, Alert, Divider, ThemeProvider, IconButton, InputAdornment } from '@mui/material';
import GoogleIcon from '@mui/icons-material/Google';
import { Eye, EyeOff } from 'lucide-react';
import { useSignUp, useSignInWithGoogle } from '@diggens/shared-hooks';
import { getFirebaseAuth, getResource } from '@diggens/api-client';
import { lightTheme } from '@diggens/shared-theme';
import { ICON_SIZES, ICON_STROKE } from '../constants/icons';

export function Signup() {
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [organization, setOrganization] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');

  const signUp = useSignUp();
  const signInWithGoogle = useSignInWithGoogle();
  const navigate = useNavigate();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    try {
      await signUp.mutateAsync({
        email,
        password,
        firstName,
        lastName,
        organization,
      });

      // Fetch user data to determine status
      const firebaseUser = getFirebaseAuth().currentUser;
      const currentUser = firebaseUser ? await getResource('users', firebaseUser.uid) : null;

      // Redirect based on user status
      if (currentUser?.status === 'pending') {
        navigate('/pending-approval');
      } else {
        navigate('/dashboard');
      }
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to sign up');
    }
  };

  const handleGoogleSignUp = async () => {
    try {
      await signInWithGoogle.mutateAsync();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to sign up with Google');
    }
  };

  return (
    <ThemeProvider theme={lightTheme}>
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh" bgcolor="grey.100">
        <Card sx={{ p: 4, maxWidth: 400, width: '100%' }}>
          <Typography variant="h4" gutterBottom>
            Sign Up
          </Typography>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <form onSubmit={handleSubmit}>
            <TextField fullWidth label="First Name" value={firstName} onChange={(e) => setFirstName(e.target.value)} margin="normal" required />
            <TextField fullWidth label="Last Name" value={lastName} onChange={(e) => setLastName(e.target.value)} margin="normal" required />
            <TextField fullWidth label="Organization" value={organization} onChange={(e) => setOrganization(e.target.value)} margin="normal" required />
            <TextField
              fullWidth
              label="Business Email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              margin="normal"
              required
              helperText="You must use your company email address"
            />
            <TextField
              fullWidth
              label="Password"
              type={showPassword ? 'text' : 'password'}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              margin="normal"
              required
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={() => setShowPassword(!showPassword)} edge="end" size="small">
                      {showPassword ? <EyeOff size={ICON_SIZES.lg} strokeWidth={ICON_STROKE} /> : <Eye size={ICON_SIZES.lg} strokeWidth={ICON_STROKE} />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <Button fullWidth variant="contained" type="submit" disabled={signUp.isPending} sx={{ mt: 2 }}>
              {signUp.isPending ? 'Creating account...' : 'Sign Up'}
            </Button>
          </form>

          <Divider sx={{ my: 2 }}>OR</Divider>

          <Button fullWidth variant="outlined" onClick={handleGoogleSignUp} disabled={signInWithGoogle.isPending} startIcon={<GoogleIcon />}>
            Sign up with Google
          </Button>

          <Typography sx={{ mt: 2 }}>
            Already have an account? <Link to="/login">Sign in</Link>
          </Typography>
        </Card>
      </Box>
    </ThemeProvider>
  );
}
